import React, { useEffect, useRef, useState } from 'react';
import { X, Download, Share2, Music, Video, Clock, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const LAYOUT_LABELS = {
  gallery_view: 'Gallery',
  focus: 'Focus',
  speaker_top: 'Speaker Top',
  speaker_side: 'Speaker Side',
  audio_only: 'Audio',
};

function formatDuration(ms) {
  if (!ms || ms < 0) return '0:00';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * ClipPlayerModal — full-screen overlay that plays a saved clip in-page
 * instead of opening the raw S3 URL in a new tab.
 *
 * Closes on: Escape key, click on the dim backdrop, or the explicit close
 * button. The video element auto-pauses on close so it doesn't keep
 * playing in the background after the modal unmounts.
 */
export default function ClipPlayerModal({ clip, onClose }) {
  const videoRef = useRef(null);
  const isAudio = clip?.layout === 'audio_only' || clip?.format === 'mp3';
  const [copied, setCopied] = useState(false);
  const copyResetTimerRef = useRef(null);

  useEffect(() => {
    if (!clip) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleKey);
    // Lock the body scroll so the page underneath doesn't scroll while the
    // modal is open — feels more "app-like".
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
      // Stop playback in case the user dismisses without clicking pause.
      const v = videoRef.current;
      if (v) {
        try { v.pause(); v.removeAttribute('src'); v.load(); } catch (_) { /* ignore */ }
      }
    };
  }, [clip, onClose]);

  // Cleanup the "Copied!" timer when the modal unmounts mid-flash. Must be
  // declared BEFORE the early-return below to keep hook ordering stable.
  useEffect(() => () => {
    if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
  }, []);

  if (!clip) return null;

  const handleShare = async () => {
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/clip-preview/${clip.shareToken}`;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch (_) { /* fall through to legacy */ }
    if (!ok) {
      // Legacy / non-secure-context fallback — execCommand still works in
      // most setups even when navigator.clipboard fails.
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_) { /* ignore */ }
    }
    if (ok) {
      setCopied(true);
      toast.success('Share link copied');
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
      copyResetTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } else {
      // Last resort — show the URL so user can copy manually.
      toast.error('Couldn\'t copy. Link: ' + url, { duration: 6000 });
    }
  };

  const Icon = isAudio ? Music : Video;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="relative w-full max-w-4xl bg-[#0a1424] border border-white/10 rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.6)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#6a1fbf]/15 border border-[#6a1fbf]/30 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#a677d9]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white truncate">{clip.name || 'Untitled clip'}</div>
            <div className="flex items-center gap-3 text-[11px] text-white/50 mt-0.5">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDuration(clip.totalDurationMs)}
              </span>
              <span>{LAYOUT_LABELS[clip.layout] || clip.layout}</span>
              {clip.segments?.length > 1 && <span>{clip.segments.length} segments</span>}
              {clip.fileSize > 0 && <span>{formatBytes(clip.fileSize)}</span>}
            </div>
          </div>
          {clip.shareSettings?.allowDownload !== false && (
            <a
              href={clip.fileUrl}
              download
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white text-xs font-medium"
              title="Download"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
          )}
          <button
            onClick={handleShare}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
              copied
                ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200'
                : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/80 hover:text-white'
            }`}
            title="Copy share link"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Copied
              </>
            ) : (
              <>
                <Share2 className="w-3.5 h-3.5" />
                Share
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Player */}
        <div className="bg-black flex items-center justify-center" style={{ aspectRatio: isAudio ? '4/1' : '16/9' }}>
          {isAudio ? (
            <div className="w-full px-6 flex flex-col items-center gap-4">
              <Music className="w-12 h-12 text-white/30" />
              <audio
                ref={videoRef}
                src={clip.fileUrl}
                controls
                autoPlay
                className="w-full max-w-md"
              />
            </div>
          ) : (
            <video
              ref={videoRef}
              src={clip.fileUrl}
              controls
              autoPlay
              playsInline
              controlsList="nodownload"
              className="w-full h-full object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
}
